import {observable} from "mobx";
import LearningAPI from "../apis/LearningAPI";
import * as APITypes from "../API"
import {
  AccountStatus,
  AccountType,
  ClassStatus,
  CreateClassInput,
  CreateRegistrationInput,
  UpdateAccountInput,
  UpdateActivityInput,
  UpdateClassInput,
  UpdateRegistrationInput,
  UpdateUserInput,
  UserStatus
} from "../API"
import {Account} from "../model/Account";
import Class from "../model/Class";
import CourseStore from "./CourseStore";
import Course from "../model/Course";
import {addYears, format, subDays} from "date-fns";
import {createUUID, getISODateFromDate, getISODateToday, isoToLocalDate, phoneToE164Format} from "./StoreUtilities";
import User from "../model/User";
import Logger from "../components/Logger";
import Registration from "../model/Registration";
import Tracking from "../components/Tracking";
import ControlTower, {Routes} from "../components/ControlTower";
import Invoice from "../model/Invoice";
import BillingAPI from "../apis/BillingAPI";
import {Activity} from "../model/Activity";
import Charge from "../model/Charge";
import CertificateAPI from "../apis/CertificateAPI";

export const AccountStoreConstants = {
  INDIVIDUAL_ACCOUNT_ID: "primary"
}

class AccountStore {
  learningAPI: LearningAPI
  billingAPI: BillingAPI
  certificateAPI: CertificateAPI
  courseStore: CourseStore

  @observable account?: Account
  @observable isLoading = true

  accounts: Account[] = []
  classes: Class[] = []
  users: User[] = []

  constructor(options: any) {
    this.learningAPI = (options && options.learningAPI) ? options.learningAPI : null
    this.billingAPI = (options && options.billingAPI) ? options.billingAPI : null
    this.certificateAPI = (options && options.certificateAPI) ? options.certificateAPI : null
    this.courseStore = (options && options.courseStore) ? options.courseStore : null
  }

  async init(account: Account) {
    this.account = account
    await this.joinCourses()
    this.isLoading = false
  }

  async loadAccount(accountId: string) {
    if (!this.account || this.account.id !== accountId) {
      console.log(`loadingAccount(${accountId})`)
      this.isLoading = true
      const accountData = await this.learningAPI.getAccount(accountId)
        .catch((err: Error) => {
          console.log(`getAccount error: ${err.message}`)
          ControlTower.route(Routes.notFound)
        })
      if (accountData) {
        const account = new Account(accountData)
        await this.init(account)
        return account
      }
    } else {
      return this.account
    }
  }

  get isAgency() {
    return this.account ? this.account.accountType === AccountType.Agency : false
  }

  get isIndividual() {
    return this.account ? this.account.accountType === AccountType.Individual : false
  }

  listAccounts = async (filter?: APITypes.ModelAccountFilterInput): Promise<Account[]> => {
    if (filter || this.accounts.length === 0) {
      const data = await this.learningAPI.listAccounts(filter)
      if (data && data.items) {
        const accounts = data.items.map((item: any) => {
          return new Account(item)
        })
        if (filter) {
          return accounts
        } else {
          this.accounts = accounts
        }
      }
    }

    return [...this.accounts]
  }

  getAccount = async (accountId: string, reload: boolean = false): Promise<Account | undefined> => {
    let account: Account | undefined

    if (!reload) {
      if (accountId === this.account!.id) {
        account = this.account
      } else {
        account = this.accounts.find((a: Account) => a.id === accountId)
      }
    }

    if (!account) {
      const data = await this.learningAPI.getAccount(accountId)
      if (data) {
        account = new Account(data)
        // Update cache
        const index = this.accounts.findIndex((a: Account) => a.id === account!.id)
        if (index >= 0) {
          this.accounts[index] = account
        } else {
          this.accounts.push(account)
        }
      }
    }

    return account
  }

  async createAccount(input:  APITypes.CreateAccountInput) {
    const account = await this.learningAPI!.createAccount(input)
    if (account) {
      Tracking.event({action: "Create Account"})
      this.account = new Account(account)
      this.isLoading = false
      this.accounts.push(this.account)
      return this.account
    } else {
      return null
    }
  }

  async updateAccount(input:  APITypes.UpdateAccountInput) {
    const result = await this.learningAPI!.updateAccount(input)
    if (result) {
      // Tracking.event({action: "Create Account"})
      const account = new Account(result)
      if (!account.classes || account.classes.length === 0) {
        account.classes = this.account!.classes
      }
      if (!account.users || account.users.length === 0) {
        account.users = this.account!.users
      }
      this.account = account

      // Update cache
      const index = this.accounts.findIndex((a: Account) => a.id === account.id)
      if (index >= 0) {
        this.accounts[index] = account
      } else {
        this.accounts.push(account)
      }
      return this.account
    } else {
      return null
    }
  }

  deleteAccount = async (account: Account) => {

    const verifyAccount = await this.getAccount(account.id, true)
    if (!verifyAccount) {
      return null
    } else {
      account = verifyAccount
    }

    if (account.users.length === 0) {
      account.users = await this.listUsers(account!.id)
    }

    let canDelete = true

    if (account.users.length > 0) {
      canDelete = false
      // Mark all users as deleted
      const promises: Promise<any>[] = []
      account.users.forEach((u: User) => {
        if (u.userStatus !== UserStatus.Deleted) {
          const update: UpdateUserInput = {
            id: u.id,
            userStatus: UserStatus.Deleted
          }
          promises.push(this.learningAPI.updateUser(update))
        }
      })
      account.users = []

      if (promises.length > 0) {
        await Promise.all(promises)
      }
    }

    if (canDelete) {
      // Check for classes
      canDelete = account.classes.length === 0
    }

    if (canDelete) {
      const result = await this.learningAPI.deleteAccount(account.id)
      if (result) {
        const index = this.accounts.findIndex((a: Account) => a.id === account.id)
        if (index >= 0) {
          this.accounts.splice(index, 1)
        }
        result.accountStatus = AccountStatus.Active // So caller will now it was actually deleted
        return new Account(result)
      }
    } else {
      // Perform a soft delete
      const update: UpdateAccountInput = {
        id: account.id,
        accountStatus: AccountStatus.Deleted
      }
      return await this.updateAccount(update)
    }

    return null
  }

  mergeAccounts = async (srcAccount: Account, dst: Account) => {
    if (srcAccount.id === dst.id) {
      throw new Error("Unable to merge account to the same account")
    }

    // Load destination account to get classes
    const dstAccount = await this.getAccount(dst.id, true)
    if (!dstAccount) {
      return null
    }

    try {
      // Migrate users
      const users = await this.listUsers(srcAccount.id)
      const userPromises: Promise<User | undefined | null>[] = users.map((user: User) => {
        const input: UpdateUserInput = {
          id: user.id,
          accountId: dst!.id
        }
        return this.updateUser(input)
      })
      await Promise.all(userPromises)

      // Migrate account activity using pagination

      let data
      let nextToken: string | undefined

      do {
        data = await this.learningAPI.listAccountActivity(srcAccount.id, undefined, 1000, nextToken)
        if (data && data.items) {
          const activityPromises: Promise<any>[] = []
          data.items.forEach((item: any) => {
            const activity = new Activity(item)
              const update: UpdateActivityInput = {
                id: activity.id,
                accountId: dstAccount.id
              }
              activityPromises.push(this.learningAPI.updateActivity(update))
          })
          await Promise.all(activityPromises)
          nextToken = data.nextToken ?? undefined
        }
      } while (data && nextToken)

      // Migrate classes & registrations

      // Get source class registrations
      await Promise.all(srcAccount.classes.map(async (classObj: Class) => {
        const srcClass = await this.getClassAndRegistrations(classObj.id)
        if (srcClass) {
          let dstClass: Class | undefined
          if (!srcClass.userId) {
            // Look for matching dest class course if class is not individually owned
            dstClass = dstAccount.classes.find((cls: Class) => (!cls.userId && cls.courseId === srcClass.courseId))
          }

          const regPromises: Promise<Registration | undefined>[] = srcClass.registrations.map((reg: Registration) => {
            const input: UpdateRegistrationInput = {
              id: reg.id,
              accountId: dstAccount.id
            }
            if (dstClass) {
              input.classId = dstClass.id
            }
            return this.updateRegistration(input)
          })
          await Promise.all(regPromises)

          let classUpdate: UpdateClassInput
          if (dstClass) {
            // Update seats in existing dest class
            if (!dstClass.seatsPurchased) {
              dstClass.seatsPurchased = 1
            }
            if (!dstClass.seatsFilled) {
              dstClass.seatsFilled = 0
            }
            classUpdate = {
              id: dstClass.id,
              seatsPurchased: dstClass.seatsPurchased + srcClass.registrations.length,
              seatsFilled: dstClass.seatsFilled + srcClass.registrations.length
            }
          } else {
            // Move source class to dest account
            classUpdate = {
              id: srcClass.id,
              accountId: dstAccount.id
            }
          }
          const updatedClass = await this.updateClass(classUpdate)

          if (updatedClass && dstClass) {
            // Delete source class
            await this.deleteClass(srcClass.id)
          }
        }
      }))

      // Delete source account
      await this.deleteAccount(srcAccount)

      // Reload destination account
      await this.getAccount(dstAccount.id, true)

      return true
    } catch (err: any) {
      Logger.error(`Error merging accounts: ${err.message}`)
      throw new Error(`Error merging accounts: ${err.message}`)
    }
  }
  
  getClass = (classId: string): Class | undefined => {
    const classObj = this.account!.classes.find((c: Class) => { return c.id === classId})
    return classObj
  }

  getClassAndRegistrations = async (classId: string): Promise<Class | undefined> => {
    const data = await this.learningAPI.getClass(classId)
    if (data) {
      const registrationCount = data.registrations && data.registrations.items ? data.registrations.items.length : 0
      if (data.seatsFilled === null || data.seatsFilled !== registrationCount) {
        // Update seatsFilled
        const input: UpdateClassInput = {
          id: data.id,
          accountId: data.accountId,
          seatsFilled: registrationCount
        }
        const result = await this.updateClass(input)
        if (result) {
          data.seatsFilled = result.seatsFilled
        }
      }
      return new Class(data)
    } else {
      return undefined
    }
  }

  async addClass(course: Course) {
    const termBegin = new Date()
    let termEnd = addYears(termBegin, 1)
    termEnd = subDays(termEnd, 1)
    const classInput: CreateClassInput = {
      courseId: course.id,
      accountId: this.account!.id,
      termBegin: getISODateFromDate(termBegin),
      termEnd: getISODateFromDate(termEnd)
    }
    const classObj = await this.learningAPI.createClass(classInput)
    if (classObj) {
      const newClass = new Class(classObj)
      newClass.course = course
      this.account!.classes.push(newClass)
      return newClass
    } else {
      return null
    }
  }

  async addPaidClass(classInput: CreateClassInput, course: Course) {
    const classObj = await this.learningAPI.createClass(classInput)
    if (classObj) {
      const newClass = new Class(classObj)
      newClass.course = course
      this.account!.classes.push(newClass)
      return newClass
    } else {
      return null
    }
  }

  async addFreeClasses() {
    const classes: Class[] = []
    const today = getISODateToday()
    const courses = await this.courseStore!.listCourses()
    if (courses && courses.length > 0) {
      for (const course of courses) {
        if (course.isFree && course.publishDate <= today && course.sunsetDate >= today) {
          const freeClass = await this.addClass(course)
          if (freeClass) {
            classes.push(freeClass)
          }
        }
      }
    }
    return classes
  }

  listClasses = async (): Promise<Class[]> => {
    if (this.account && this.account.classes) {
      await this.joinCourses()
      return [...this.account.classes]
    } else {
      return []
    }
  }

   async createClass(input:  APITypes.CreateClassInput) {
    const result = await this.learningAPI!.createClass(input)
    if (result) {
      return new Class(result)
    } else {
      return null
    }
  }

  async updateClass(input:  APITypes.UpdateClassInput) {
    const result = await this.learningAPI!.updateClass(input)
    if (result) {
      // Tracking.event({action: "Create Account"})
      let found = this.account!.classes.find((c: Class) => c.id === result.id)
      if (found) {
        found.update(result)
      } else {
        found = new Class(result)
      }
      return found
    } else {
      return null
    }
  }

  async deleteClass(id: string) {

    const result = await this.learningAPI!.deleteClass(id)
      .catch(err => {
        Logger.error("Unable to delete class")
      })

    if (result) {
      const classObj = new Class(result)
      return classObj
    } else {
      return null
    }
  }
  
  async addClassRegistration(input: CreateRegistrationInput) {

    // Verify seat availability in class
    const classObj = this.getClass(input.classId)
    if (!classObj) {
      throw new Error("Unable to find class")
    }

    if (!classObj.course!.isFree) {
      if (!classObj.seatsPurchased || (classObj.seatsFilled && classObj.seatsFilled >= classObj.seatsPurchased)) {
        throw new Error(`${classObj.course!.title} is full. Please add more seats.`)
      }
    }

    const result = await this.learningAPI!.createRegistration(input)
      .catch(err => {
        Logger.error("Unable to create class registration")
      })

    if (result) {
      const registration = new Registration(result)
      this.joinClassToRegistration(registration)
      return registration
    } else {
      return null
    }
  }

  async deleteClassRegistration(id: string) {

    const result = await this.learningAPI!.deleteRegistration(id)
      .catch(err => {
        Logger.error("Unable to delete class registration")
      })

    if (result) {
      const registration = new Registration(result)
      return registration
    } else {
      return null
    }
  }

  async createCertificate(registration: Registration): Promise<Blob> {

    const completedDate = isoToLocalDate(registration.completedAt)
    const result = await this.certificateAPI.create("StandardCertificateForm.pdf",
      {
        student: `${registration.user?.firstName} ${registration.user?.lastName}`,
        course: registration.class?.course?.title,
        day: format(completedDate, "do"),
        monthYear: format(completedDate, "MMMM, yyyy")
      })

    return result
  }

  async joinCourses() {
    if (this.account && this.account.classes) {
      const courses = await this.courseStore!.listCourses()

      this.account.classes.forEach((c: Class) => {
        const courseFound = courses.find((course: Course) => course.id === c.courseId)
        if (courseFound) {
          c.course = courseFound
        }
      })
      // Sort classes by course title
      this.account.classes.sort((a: Class, b: Class) => {
        if (a.course && b.course) {
          return a.course.title.localeCompare(b.course.title)
        } else {
          return a.id.localeCompare(b.id)
        }
      })

    }
  }

  listUsers = async (accountId: string): Promise<User[]> => {
    let users: User[] = []

    if (accountId) {
      let account = await this.getAccount(accountId)

      if (account) {
        if (account.users.length === 0) {
          // Filter out deleted users
          // const filter: ModelUserFilterInput = {
          //   userStatus: {"ne": UserStatus.Deleted}
          // }
          const data = await this.learningAPI.getAccountUsers(accountId /*, filter */)
          if (data) {
            await account.loadUsers(data.users!.items!)
          }
        }

        users = [...account.users]
      }
    }

    return users
  }

  getUser = async (userId: string, accountId?: string): Promise<User | undefined> => {
    let user : User | undefined

    if (accountId) {
      let account = await this.getAccount(accountId)
      if (account) {
        user = account.users.find((u: User) => u.id === userId)
      }
    }

    if (!user) {
      const data = await this.learningAPI.getUser(userId)
      if (data) {
        user = new User(data)
        this.joinClassesToUser(user)
      }
    }

    return user
  }

  loadUser = async (userId: string, accountId?: string): Promise<User | undefined> => {
    let user : User | undefined

    if (accountId) {
      let account = await this.getAccount(accountId)
      if (account) {
        user = account.users.find((u: User) => u.id === userId)
      }
    }

    if (!user || !user.account) {
      const data = await this.learningAPI.loadUser(userId)
      if (data) {
        user = new User(data)
        this.joinClassesToUser(user)
      }
    }

    return user
  }

  joinClassesToUser = (user: User) => {
    if (this.account && this.account.classes && user.registrations.length > 0) {
      user.registrations.forEach((r: Registration) => {
        r.class = this.findClass(r.classId)
      })
    }
  }

  joinClassToRegistration = (registration: Registration) => {
    if (!registration.class) {
      registration.class = this.findClass(registration.classId)
    }
  }

  findUser = async (email: string): Promise<User | undefined> => {
    let user
    const users = await this.listUsers(this.account!.id)
    if (users && users.length > 0) {
      const search = email.toLowerCase()
      user = users.find((u: User) => {
        return u.email === search
      })
    }
    return user
  }

  findClass = (id: string) => {
    return this.account!.classes.find((c: Class) => c.id === id)
  }

  async updateUser(input: APITypes.UpdateUserInput) {
    if (input.phone) {
      input.phone = phoneToE164Format(input.phone)
    } else {
      input.phone = null
    }

    const update = await this.learningAPI!.updateUser(input)
    if (update) {
       const user = new User(update)
      if (user.accountId === this.account!.id) {
        // Update list
        const index = this.account!.users.findIndex((u: User) => { return u.id === input.id})
        if (index >= 0) {
          this.account!.users[index] = user
        }
      } else if (input.accountId) {
        // See if user was moved to another account
        const index = this.account!.users.findIndex((u: User) => { return u.id === input.id})
        if (index >= 0) {
          this.account!.users.splice(index, 1)

          const newAccount = this.accounts.find((a: Account) => a.id === user.accountId)
          if (newAccount) {
            newAccount.users.push(user)
          }
        }
      }
      return user
    } else {
      return null
    }
  }

  async createUser(input: APITypes.CreateUserInput) {
    // Verify email is not in use
    const existingUser = await this.findUser(input.email)
    if (existingUser) {
      throw Error("This email is already in use")
    }

    input.id = createUUID()

    if (input.phone) {
      input.phone = phoneToE164Format(input.phone)
    } else {
      input.phone = null
    }

    const data = await this.learningAPI!.createUser(input)
    if (data) {
      Tracking.event({action: "Create User"})
      const user = new User(data)
      this.account!.users.push(user)
      return user
    } else {
      return null
    }
  }

  async deleteUser(user: User) {
    let deletedUser: User | null = null

    if (user.userStatus === UserStatus.Inactive || user.userStatus === UserStatus.Invited) {
      let result = await this.learningAPI!.deleteUser(user.id)
      if (result) {
        deletedUser = new User(result)
        // Remove from list
        const index = this.account!.users.findIndex((u: User) => u.id === deletedUser!.id)
        if (index >= 0) {
          this.account!.users.splice(index, 1)
        }
      }
    } else {
      // Soft delete
      const update: APITypes.UpdateUserInput = {
        id: user.id,
        userStatus: UserStatus.Deleted
      }
      deletedUser = await this.updateUser(update)
    }

    return deletedUser
  }

  async migrateUserToAccount(user: User, account: Account) {
    // Move Registrations and Classes

    // Migrate registrations and classes
    if (user.registrations.length > 0) {
      const newRegistrations: Registration[] = []

      for (const r of user.registrations) {
        if (r.class) {
          const oldClass = r.class
          let newClass: Class | undefined | null

          const registrationUpdate: UpdateRegistrationInput = {
            id: r.id,
            accountId: account.id
          }

          if (user.account!.accountType === AccountType.Individual ||
            (oldClass.ownerId === user.id) /* ||
            (oldClass.seatsPurchased === 1 || (!oldClass.seatsPurchased && oldClass.seatsFilled === 1) || !oldClass.seatsFilled) */) {
            // Just move the class over to the new account
            const classUpdate: UpdateClassInput = {
              id: r.classId,
              accountId: account.id,
              ownerId: user.id,
              userId: user.id
            }
            newClass = await this.updateClass(classUpdate)
            if (this.account?.id === oldClass.accountId) {
            // Remove cached class
              const classIndex = this.account!.classes.findIndex((c: Class) => c.id === oldClass.id)
              if (classIndex >= 0) {
                this.account!.classes.splice(classIndex, 1)
              }
            }
          } else if (r.classStatus === ClassStatus.NotStarted) {
            // Increment the class seatsFilled and delete the registration
            const classUpdate: UpdateClassInput = {
              id: r.classId,
              seatsFilled: oldClass.seatsFilled ? oldClass.seatsFilled-1 : 0
            }
            await this.updateClass(classUpdate)
            await this.deleteClassRegistration(r.id)
          } else {
            // Make an individual copy of the class
            const classCreate: CreateClassInput = {
              courseId: oldClass.courseId,
              accountId: account.id,
              ownerId: user.id,
              userId: user.id,
              termBegin: oldClass.termBegin,
              termEnd: oldClass.termEnd,
              seatsPurchased: 1,
              seatsFilled: 1,
              couponCode: oldClass.couponCode,
              assignmentSchedule: oldClass.assignmentSchedule
            }
            newClass = await this.createClass(classCreate)
          }

          if (newClass) {
            registrationUpdate.classId = newClass.id
            const registration = await this.updateRegistration(registrationUpdate)
            if (registration) {
              newClass.course = await this.courseStore.getCourse(newClass.courseId)
              registration.class = newClass
              newRegistrations.push(registration)
            }
          }
        }
      }
      user.registrations = newRegistrations
    }

    // Migrate activity
    const activityFilter: APITypes.ModelActivityFilterInput = {
      userId: {eq: user.id}
    }
    const activity = await this.listAccountActivity(user.accountId, activityFilter)

    const promises: Promise<any>[] = []
    activity.forEach((a: Activity) => {
      const update: UpdateActivityInput = {
        id: a.id,
        accountId: account.id
      }
      promises.push(this.learningAPI.updateActivity(update))
    })

    await Promise.all(promises)

    return true
  }

  updateRegistration = async (input: APITypes.UpdateRegistrationInput) => {
    let registration = undefined
    const result = await this.learningAPI.updateRegistration(input)
    if (result) {
      registration = new Registration(result)
    }
    return registration
  }



  // Billing Related Methods

  createInvoice = async (user: User, course: Course, couponId: string | undefined, quantity: number, amount: number, tokenId: string | undefined) => {
    const invoice = new Invoice({
      customer: this.account!.customerId,
      collection_method: "charge_automatically",
      auto_advance: true,
      items: [
        {
          customer: this.account!.customerId,
          unit_amount: Math.round(amount * 100),
          quantity: quantity,
          currency: "usd",
          description: course.title,
        }
      ]
    })

    if (couponId) {
      invoice.items[0].discounts = [
        {coupon: couponId}
      ]
    }

    const result = await this.billingAPI.createInvoice(user, this.account!, invoice, tokenId)

    if (!this.account!.customerId && result && result.customer) {
      const input: UpdateAccountInput = {
        id: this.account!.id,
        customerId: result.customer
      }
      const account = await this.updateAccount(input)
      if (account) {
        this.account!.customerId = account.customerId
      }
    }
    // console.log(`createInvoice = ${JSON.stringify(result)}`)

    return (result)
  }

  getCustomer = async (id: string) => {
    const result = await this.billingAPI.getCustomer(id)
    return result
  }

  deleteSource = async (sourceId: string) => {
    if (this.account!.customerId) {
      return await this.billingAPI.deleteSource(this.account!.customerId, sourceId)
    } else {
      return null
    }
  }

  // Charges methods

  listCharges = async (accountId: string) => {
    let charges: Charge[] = []

    const account = await this.getAccount(accountId)
    if (account && account.customerId) {
      const result = await this.billingAPI.getCharges(account.customerId)
      if (result && result.data) {
        charges = result.data.map((item: any) => {
          return new Charge(item)
        })
      }
    }

    return charges
  }

  // Activity related methods

  listAccountActivity = async (accountId: string, filter?: APITypes.ModelActivityFilterInput): Promise<Activity[]> => {
    let data
    let nextToken: string | undefined
    let activities: Activity[] = []

    do {
      data = await this.learningAPI.listAccountActivity(accountId, filter, 1000, nextToken)
      if (data && data.items) {
        data.items.forEach((item: any) => {
          activities.push(new Activity(item))
        })
        nextToken = data.nextToken ?? undefined
      }
    } while (data && nextToken)

    await this.joinActivities(activities)

    return activities
  }

  listActivityByDate = async (date: string, filter?: APITypes.ModelActivityFilterInput): Promise<Activity[]> => {
    let data
    let nextToken: string | undefined
    let activities: Activity[] = []

    do {
      data = await this.learningAPI.listActivityByDate(date, filter, 1000, nextToken)
      if (data && data.items) {
        data.items.forEach((item: any) => {
          activities.push(new Activity(item))
        })
        nextToken = data.nextToken ?? undefined
      }
    } while (data && nextToken)

    await this.joinActivities(activities)

    return activities
  }

  listActivityByAccount = async (accountId: string, activityDate: APITypes.ModelStringKeyConditionInput,
                                 sortDirection?: APITypes.ModelSortDirection, filter?: APITypes.ModelActivityFilterInput): Promise<Activity[]> => {
    let data
    let nextToken: string | undefined
    let activities: Activity[] = []

    do {
      data = await this.learningAPI.listActivityByAccount(accountId, activityDate, sortDirection, filter, 1000, nextToken)
      if (data && data.items) {
        data.items.forEach((item: any) => {
          activities.push(new Activity(item))
        })
        nextToken = data.nextToken ?? undefined
      }
    } while (data && nextToken)

    await this.joinActivities(activities)

    return activities
  }

  joinActivities = async (activities: Activity[]) => {
    if (this.accounts.length === 0) {
      await this.listAccounts()
    }

    for (const a of activities) {
      a.account = await this.getAccount(a.accountId)
      if (a.account && a.account.users.length === 0) {
        await this.listUsers(a.accountId)
      }
      a.user = await this.getUser(a.userId, a.accountId)
    }
  }

}

export default AccountStore